import { useMutation } from '@tanstack/react-query';
import {
  updateTheme,
  createSiteFromTemplate,
  createSitelessMetasite,
  installStoresApp,
  attachTemplate,
  setupModalystProducts,
  setupModalystProductsByCategory,
  installApps,
  resetStore,
  deleteAllProducts,
} from './endpoints';
import { useMsId, useSetMsId, useSetMetasiteError, useSelectedThemeColor } from '../../../store/appStore';
import { templateSelectionKeys } from './keys';
import { MODALYST_APP_DEF_ID } from '@common/constants/apps';

export function useUpdateTheme() {
  const metaSiteId = useMsId();
  const themeKey = useSelectedThemeColor();

  return useMutation({
    mutationKey: templateSelectionKeys.mutations.updateTheme(),
    mutationFn: () => updateTheme(metaSiteId, themeKey),
    onError: (error) => {
      console.error('Failed to update theme:', error);
    },
  });
}

export function useCreateSiteFromTemplate() {
  const setMsId = useSetMsId();
  const setMetasiteError = useSetMetasiteError();

  return useMutation({
    mutationKey: templateSelectionKeys.mutations.createSite(),
    mutationFn: ({ category, lang }: { category: string; lang?: string }) =>
      createSiteFromTemplate(category, lang),
    onSuccess: (metaSiteId) => {
      setMsId(metaSiteId);
    },
    onError: (error) => {
      console.error('Failed to create site:', error);
      setMetasiteError(true);
    },
  });
}

export function useCreateSitelessMetasite() {
  const setMsId = useSetMsId();
  const setMetasiteError = useSetMetasiteError();

  return useMutation({
    mutationKey: templateSelectionKeys.mutations.createSitelessMetasite(),
    mutationFn: () => createSitelessMetasite(),
    onSuccess: (metaSiteId) => {
      setMsId(metaSiteId);
    },
    onError: (error) => {
      console.error('Failed to create siteless metasite:', error);
      setMetasiteError(true);
    },
  });
}

export function useInstallStoresApp() {
  return useMutation({
    mutationKey: templateSelectionKeys.mutations.installStoresApp(),
    mutationFn: ({ metaSiteId }: { metaSiteId: string }) => installStoresApp(metaSiteId),
    onError: (error) => {
      console.error('Failed to install stores app:', error);
    },
  });
}

export function useAttachTemplate() {
  return useMutation({
    mutationKey: templateSelectionKeys.mutations.attachTemplate(),
    mutationFn: ({ metaSiteId, category, lang }: { metaSiteId: string; category: string; lang?: string }) =>
      attachTemplate(metaSiteId, category, lang),
    onError: (error) => {
      console.error('Failed to attach template:', error);
    },
  });
}

export function useSetupModalystProducts() {
  return useMutation({
    mutationKey: templateSelectionKeys.mutations.setupProducts(),
    mutationFn: ({ metaSiteId, productList }: { metaSiteId: string; productList: string[] }) =>
      setupModalystProducts(metaSiteId, productList),
    onError: (error) => {
      console.error('Failed to setup products:', error);
    },
  });
}

export function useSetupModalystProductsByCategory() {
  return useMutation({
    mutationKey: templateSelectionKeys.mutations.setupProducts(),
    mutationFn: ({ category }: { category: string }) => setupModalystProductsByCategory(category),
    onError: (error) => {
      console.error('Failed to setup products by category:', error);
    },
  });
}

export function useInstallApps() {
  return useMutation({
    mutationKey: templateSelectionKeys.mutations.installApps(),
    mutationFn: ({ metaSiteId }: { metaSiteId: string }) => installApps(metaSiteId, [MODALYST_APP_DEF_ID]),
    onError: (error) => {
      console.error('Failed to install apps:', error);
    },
  });
}

export function useResetStore() {
  return useMutation({
    mutationKey: templateSelectionKeys.mutations.resetStore(),
    mutationFn: ({ metaSiteId }: { metaSiteId: string }) => resetStore(metaSiteId),
    onError: (error) => {
      console.error('Failed to reset store:', error);
    },
  });
}

export function useDeleteAllProducts() {
  return useMutation({
    mutationKey: templateSelectionKeys.mutations.deleteAllProducts(),
    mutationFn: () => deleteAllProducts(),
    onError: (error) => {
      console.error('Failed to delete all products:', error);
    },
  });
}
